import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useIsMobile } from "@/hooks/use-mobile";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Loader2, Send, Info, Smartphone, Upload, BookUser } from "lucide-react";
import { SharedContactBubble } from "@/components/chat/SharedContactBubble";
import {
  buildContactPreviewMetadata,
  sanitizeFabContactCanonical,
  validateFabContactCanonical,
  type FabContactCanonical,
} from "@/lib/whatsapp-contact-message";
import { isDeviceContactPickerSupported, pickDeviceContact } from "@/lib/contact-picker";
import { parseVCardFile } from "@/lib/vcard-parser";
import type { WhatsAppProviderId } from "@/lib/whatsapp-provider-capabilities";

interface LeadOption {
  id: string;
  name: string;
  phone: string | null;
  email: string | null;
}

type ContactTab = "device" | "lead" | "manual";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  conversationId: string;
  workspaceId: string;
  memberId?: string | null;
  contextMessageId?: string | null;
  provider: WhatsAppProviderId;
}

export function SendWhatsAppContactDialog({
  open,
  onOpenChange,
  conversationId,
  workspaceId,
  memberId,
  contextMessageId,
  provider,
}: Props) {
  const { toast } = useToast();
  const isMobile = useIsMobile();
  const vcfInputRef = useRef<HTMLInputElement>(null);

  const [tab, setTab] = useState<ContactTab>("device");
  const [importedContact, setImportedContact] = useState<FabContactCanonical | null>(null);
  const [pickingDevice, setPickingDevice] = useState(false);
  const [importingFile, setImportingFile] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");
  const [results, setResults] = useState<LeadOption[]>([]);
  const [searching, setSearching] = useState(false);
  const [selectedLead, setSelectedLead] = useState<LeadOption | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [org, setOrg] = useState("");
  const [sending, setSending] = useState(false);

  const devicePickerSupported = isDeviceContactPickerSupported();

  useEffect(() => {
    if (!open) {
      setTab("device");
      setImportedContact(null);
      setPickingDevice(false);
      setImportingFile(false);
      setSearchTerm("");
      setResults([]);
      setSelectedLead(null);
      setName("");
      setPhone("");
      setEmail("");
      setOrg("");
      setSending(false);
    }
  }, [open]);

  const searchLeads = useCallback(
    async (term: string) => {
      if (!workspaceId || term.length < 2) {
        setResults([]);
        setSearching(false);
        return;
      }
      setSearching(true);
      try {
        const { data, error } = await supabase.rpc("search_leads_by_term", {
          p_workspace_id: workspaceId,
          p_term: term,
          p_limit: 20,
          p_funnel_id: null,
          p_include_null_funnel: false,
        });
        if (error) throw error;
        setResults(
          ((data ?? []) as Array<{ id: string; name: string | null; phone: string | null; email: string | null }>).map(
            (l) => ({ id: l.id, name: l.name, phone: l.phone, email: l.email }),
          ) as LeadOption[],
        );
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    },
    [workspaceId],
  );

  useEffect(() => {
    if (!open || tab !== "lead") return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => searchLeads(searchTerm.trim()), 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [open, tab, searchTerm, searchLeads]);

  const draftContact = useMemo((): FabContactCanonical | null => {
    if (tab === "device") return importedContact;
    if (tab === "lead") {
      if (!selectedLead?.phone) return null;
      return sanitizeFabContactCanonical({
        name: selectedLead.name,
        phones: [selectedLead.phone],
        ...(selectedLead.email ? { emails: [selectedLead.email] } : {}),
      });
    }
    return sanitizeFabContactCanonical({
      name,
      phones: phone ? [phone] : [],
      ...(email.trim() ? { emails: [email.trim()] } : {}),
      ...(org.trim() ? { org: org.trim() } : {}),
    });
  }, [tab, importedContact, selectedLead, name, phone, email, org]);

  const emptyMessage = useMemo(() => {
    if (tab === "device") return "Escolha um contato da agenda ou importe um arquivo .vcf.";
    if (tab === "lead") return "Selecione um lead do CRM.";
    return "Preencha os dados do contato.";
  }, [tab]);

  const validationError = useMemo(
    () => (draftContact ? validateFabContactCanonical(draftContact) : emptyMessage),
    [draftContact, emptyMessage],
  );

  const previewMetadata = useMemo(
    () => (draftContact && !validateFabContactCanonical(draftContact) ? buildContactPreviewMetadata(draftContact) : null),
    [draftContact],
  );

  const handlePickFromDevice = async () => {
    if (!devicePickerSupported) {
      toast({
        title: "Agenda não disponível",
        description: "Use a importação de arquivo .vcf ou preencha manualmente.",
        variant: "destructive",
      });
      return;
    }

    setPickingDevice(true);
    try {
      const contact = await pickDeviceContact();
      if (!contact) return;
      setImportedContact(contact);
      toast({ title: "Contato selecionado", description: contact.name });
    } catch {
      toast({
        title: "Não foi possível abrir a agenda",
        description: "Tente importar um arquivo .vcf ou preencher manualmente.",
        variant: "destructive",
      });
    } finally {
      setPickingDevice(false);
    }
  };

  const handleVcfFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    setImportingFile(true);
    try {
      const contact = await parseVCardFile(file);
      if (!contact) {
        toast({
          title: "Arquivo inválido",
          description: "Não encontramos um contato com telefone neste .vcf.",
          variant: "destructive",
        });
        return;
      }
      setImportedContact(contact);
      setTab("device");
      toast({ title: "Contato importado", description: contact.name });
    } catch {
      toast({
        title: "Erro ao ler arquivo",
        description: "Verifique se o arquivo é um vCard (.vcf) válido.",
        variant: "destructive",
      });
    } finally {
      setImportingFile(false);
    }
  };

  const handleSend = async () => {
    if (!draftContact || validateFabContactCanonical(draftContact)) return;
    setSending(true);

    const { data, error } = await supabase.functions.invoke("send-contact-message", {
      body: {
        conversation_id: conversationId,
        member_id: memberId || undefined,
        context_message_id: contextMessageId || undefined,
        contact: draftContact,
      },
    });

    setSending(false);

    if (error || (data as any)?.error) {
      toast({
        title: "Falha ao enviar",
        description:
          (data as any)?.friendly_message ||
          (data as any)?.error ||
          error?.message ||
          "Erro desconhecido",
        variant: "destructive",
      });
      return;
    }

    if ((data as any)?.success === false) {
      toast({
        title: "Não foi possível enviar",
        description: (data as any)?.friendly_message || "Tente novamente.",
        variant: "destructive",
      });
      return;
    }

    toast({ title: "Contato enviado" });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Enviar contato</DialogTitle>
          <DialogDescription>
            Compartilhe um cartão de contato da agenda do celular, de um arquivo .vcf ou do CRM.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {provider === "whatsapp_cloud" && (
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription className="text-xs">
                Na API Cloud da Meta, contatos só podem ser enviados dentro da janela de atendimento de
                24 horas.
              </AlertDescription>
            </Alert>
          )}

          <Tabs value={tab} onValueChange={(v) => setTab(v as ContactTab)}>
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="device">Agenda / Arquivo</TabsTrigger>
              <TabsTrigger value="lead">CRM</TabsTrigger>
              <TabsTrigger value="manual">Manual</TabsTrigger>
            </TabsList>

            <TabsContent value="device" className="mt-4 space-y-3">
              <div className="grid grid-cols-1 gap-2">
                {devicePickerSupported && (
                  <Button
                    type="button"
                    variant="outline"
                    className="h-auto min-h-11 w-full items-start justify-start gap-2 whitespace-normal px-3 py-2.5 text-left"
                    onClick={() => void handlePickFromDevice()}
                    disabled={pickingDevice || importingFile}
                  >
                    {pickingDevice ? (
                      <Loader2 className="mt-0.5 h-4 w-4 shrink-0 animate-spin" />
                    ) : (
                      <Smartphone className="mt-0.5 h-4 w-4 shrink-0" />
                    )}
                    <span className="flex min-w-0 flex-1 flex-col items-start gap-0.5 overflow-hidden text-left">
                      <span className="w-full text-sm font-medium leading-snug">Da agenda do celular</span>
                      <span className="w-full text-pretty text-xs font-normal leading-snug text-muted-foreground">
                        Abre os contatos do aparelho
                      </span>
                    </span>
                  </Button>
                )}

                <Button
                  type="button"
                  variant="outline"
                  className="h-auto min-h-11 w-full items-start justify-start gap-2 whitespace-normal px-3 py-2.5 text-left"
                  onClick={() => vcfInputRef.current?.click()}
                  disabled={pickingDevice || importingFile}
                >
                  {importingFile ? (
                    <Loader2 className="mt-0.5 h-4 w-4 shrink-0 animate-spin" />
                  ) : (
                    <Upload className="mt-0.5 h-4 w-4 shrink-0" />
                  )}
                  <span className="flex min-w-0 flex-1 flex-col items-start gap-0.5 overflow-hidden text-left">
                    <span className="w-full text-sm font-medium leading-snug">Importar .vcf</span>
                    <span className="w-full text-pretty text-xs font-normal leading-snug text-muted-foreground">
                      Arquivo do computador ou exportado do celular
                    </span>
                  </span>
                </Button>
              </div>

              <input
                ref={vcfInputRef}
                type="file"
                accept=".vcf,text/vcard,text/x-vcard,text/directory;profile=vcard"
                className="hidden"
                onChange={(e) => void handleVcfFileChange(e)}
              />

              {!devicePickerSupported && (
                <Alert>
                  <BookUser className="h-4 w-4" />
                  <AlertDescription className="text-xs">
                    {isMobile
                      ? "Neste navegador não é possível abrir a agenda diretamente. Exporte o contato como .vcf nas configurações do telefone ou use a aba Manual."
                      : "No computador, exporte um contato do Outlook, Google Contacts ou Agenda (macOS) como arquivo .vcf e importe aqui."}
                  </AlertDescription>
                </Alert>
              )}

              {importedContact && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-8 px-2 text-xs text-muted-foreground"
                  onClick={() => setImportedContact(null)}
                >
                  Limpar seleção
                </Button>
              )}
            </TabsContent>

            <TabsContent value="lead" className="mt-4 space-y-3">
              <Command shouldFilter={false} className="rounded-lg border">
                <CommandInput
                  placeholder="Buscar lead por nome, telefone ou e-mail..."
                  value={searchTerm}
                  onValueChange={setSearchTerm}
                />
                <CommandList>
                  {searching ? (
                    <div className="flex items-center justify-center py-6 text-sm text-muted-foreground">
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Buscando...
                    </div>
                  ) : searchTerm.trim().length < 2 ? (
                    <CommandEmpty>Digite ao menos 2 caracteres para buscar.</CommandEmpty>
                  ) : results.length === 0 ? (
                    <CommandEmpty>Nenhum lead encontrado.</CommandEmpty>
                  ) : (
                    <CommandGroup>
                      {results.map((lead) => (
                        <CommandItem
                          key={lead.id}
                          value={lead.id}
                          onSelect={() => setSelectedLead(lead)}
                          className={selectedLead?.id === lead.id ? "bg-accent" : ""}
                        >
                          <div className="flex min-w-0 flex-col">
                            <span className="truncate font-medium">{lead.name}</span>
                            <span className="truncate text-xs text-muted-foreground">
                              {lead.phone || "Sem telefone"}
                              {lead.email ? ` · ${lead.email}` : ""}
                            </span>
                          </div>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  )}
                </CommandList>
              </Command>

              {selectedLead && !selectedLead.phone && (
                <p className="text-xs text-destructive">
                  Este lead não tem telefone cadastrado. Use outra aba ou atualize o lead.
                </p>
              )}
            </TabsContent>

            <TabsContent value="manual" className="mt-4 space-y-3">
              <div className="space-y-1.5">
                <Label htmlFor="contact-send-name">Nome</Label>
                <Input
                  id="contact-send-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Nome completo"
                  maxLength={256}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="contact-send-phone">Telefone</Label>
                <Input
                  id="contact-send-phone"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="5511999999999"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="contact-send-email">E-mail (opcional)</Label>
                <Input
                  id="contact-send-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="contato@empresa.com"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="contact-send-org">Empresa (opcional)</Label>
                <Input
                  id="contact-send-org"
                  value={org}
                  onChange={(e) => setOrg(e.target.value)}
                  placeholder="Nome da empresa"
                  maxLength={256}
                />
              </div>
            </TabsContent>
          </Tabs>

          {previewMetadata && (
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Prévia</Label>
              <SharedContactBubble
                metadata={previewMetadata}
                workspaceId={workspaceId}
                isUserMessage={false}
              />
            </div>
          )}

          {draftContact && validateFabContactCanonical(draftContact) && (
            <p className="text-xs text-destructive">{validateFabContactCanonical(draftContact)}</p>
          )}
          {!draftContact && tab !== "manual" && (
            <p className="text-xs text-muted-foreground">{emptyMessage}</p>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={sending}>
            Cancelar
          </Button>
          <Button
            onClick={handleSend}
            disabled={sending || !draftContact || !!validateFabContactCanonical(draftContact)}
          >
            {sending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
            Enviar contato
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
